/* Product form: resolve the selected variant from the option radios and update
   price, availability, URL and gallery. Unavailable combinations are computed
   in Liquid at render time, so this only handles what changes after load. */
class ProductFormGp extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-variant-json]');
    try {
      this.variants = JSON.parse(json.textContent);
    } catch (e) {
      return;
    }
    this.input = this.querySelector('[data-variant-input]');
    this.priceEl = this.querySelector('[data-price]');
    this.addButton = this.querySelector('[data-add-button]');
    this.bis = this.querySelector('[data-bis]');
    this.lowStock = this.querySelector('[data-low-stock]');
    this.lowStockCta = this.querySelector('[data-low-stock-cta]');
    const tagJson = this.querySelector('[data-bis-tags]');
    try {
      this.bisTags = tagJson ? JSON.parse(tagJson.textContent) : {};
    } catch (e) {
      this.bisTags = {};
    }
    const priceJson = this.querySelector('[data-variant-prices]');
    try {
      this.prices = priceJson ? JSON.parse(priceJson.textContent) : {};
    } catch (e) {
      this.prices = {};
    }
    const preorderJson = this.querySelector('[data-variant-preorder]');
    try {
      this.preorder = preorderJson ? JSON.parse(preorderJson.textContent) : null;
    } catch (e) {
      this.preorder = null;
    }
    this.preorderLine = this.querySelector('[data-preorder-line]');
    this.quantityInput = this.querySelector('input[name="quantity"]');
    this.inCart = {};
    this.addEventListener('change', (e) => {
      if (e.target.matches('input[data-option-index]')) this.onOptionChange();
      else if (e.target === this.quantityInput) this.refreshCurrent();
    });

    /* The loaded variant's pre-order line, which Liquid leaves empty. */
    this.current = this.input && this.variants.find((v) => String(v.id) === this.input.value);
    if (this.current) this.refresh(this.current);

    /* What the cart already holds of each variant, so the stock left to add
       is what's on hand minus that. Read on load and after every cart change
       (pubsub.js and constants.js are global and deferred ahead of this). */
    this.loadCart();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.loadCart());
    }

    const gallery = document.querySelector('[data-gallery-main]');
    if (gallery) {
      document.querySelectorAll('[data-gallery-thumb]').forEach((btn) => {
        btn.addEventListener('click', () => this.showMedia(btn.dataset.mediaId));
      });
    }
  }

  selectedOptions() {
    return Array.from(this.querySelectorAll('input[data-option-index]:checked'))
      .sort((a, b) => a.dataset.optionIndex - b.dataset.optionIndex)
      .map((i) => i.value);
  }

  onOptionChange() {
    const chosen = this.selectedOptions();
    const variant = this.variants.find(
      (v) => v.options.length === chosen.length && v.options.every((o, i) => o === chosen[i])
    );
    if (!variant) return;

    if (this.input) this.input.value = variant.id;

    /* Prices come from the Liquid-built map, never formatted here — the
       shop sells into markets whose presentment currency is not AUD. A
       missing entry leaves the existing text rather than writing a wrong
       one. */
    const priceText = this.prices[variant.id];
    if (this.priceEl && priceText) this.priceEl.textContent = priceText;

    this.current = variant;
    const preorder = this.refresh(variant);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    this.toggleBackInStock(variant);

    /* A variant without its own media leaves the gallery alone rather than
       blanking it or jumping to the first shot. Sand and Forest Pebbles share
       one image across ten variants each, so this is the common case. */
    if (variant.featured_media && variant.featured_media.id) {
      this.showMedia(String(variant.featured_media.id));
    }

    /* Announced last, after the back-in-stock form has been toggled, so a
       listener (the sticky bar below) sees the page already in its new
       state. The price is the Liquid-formatted string from the map above,
       or null when the map has no entry — never formatted here. */
    this.dispatchEvent(
      new CustomEvent('gp:variant-change', {
        bubbles: true,
        detail: { variant, price: priceText || null, preorder },
      })
    );
  }

  async loadCart() {
    try {
      const response = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      const counts = {};
      (cart.items || []).forEach((item) => {
        counts[item.variant_id] = (counts[item.variant_id] || 0) + item.quantity;
      });
      this.inCart = counts;
    } catch (e) {
      return;
    }
    this.refreshCurrent();
  }

  /* A cart or quantity change: re-render the current variant and tell the
     sticky bar, whose label follows the pre-order state. */
  refreshCurrent() {
    if (!this.current) return;
    const preorder = this.refresh(this.current);
    this.dispatchEvent(
      new CustomEvent('gp:variant-change', {
        bubbles: true,
        detail: { variant: this.current, price: null, preorder },
      })
    );
  }

  /* From the Liquid-built map: tracked, none left, "Continue selling when
     out of stock" ticked. */
  isPreorder(variant) {
    return Boolean(this.preorder && this.preorder.variants && this.preorder.variants[variant.id]);
  }

  /* The add button, the low-stock lines and the pre-order line,
     for a variant, the quantity chosen and what the cart already holds.
     Stock left to add is what's on hand less the cart's share, so:
     - none left, and Continue selling ticked: Pre-order, as for a variant
       at zero, with "Made to order, ships by …";
     - fewer left than the quantity chosen: still Add to cart, but the line
       reads "Some are made to order - these ship by …";
     - the low-stock lines count what's left after the cart's share.
     Nothing marks the add itself: the cart works out its own pre-order note
     from the line's quantity and the stock on hand
     (snippets/cart-stock-note-gp.liquid), so it follows every + and -.
     Returns whether the variant is on pre-order. */
  refresh(variant) {
    const stock = this.preorder && this.preorder.stock ? this.preorder.stock[variant.id] : null;
    const left = stock ? stock.qty - (this.inCart[variant.id] || 0) : null;
    const quantity = Math.max(1, Number(this.quantityInput && this.quantityInput.value) || 1);
    let preorder = this.isPreorder(variant);
    let partial = false;
    if (stock && stock.continue && variant.available) {
      if (left <= 0) preorder = true;
      else if (quantity > left) partial = true;
    }

    if (this.addButton) {
      this.addButton.disabled = !variant.available;
      /* The label now lives in a <span> beside .loading__spinner (Ruling C2,
         for assets/product-form.js's contract) — writing textContent on the
         button itself would delete the spinner node along with the old
         label. */
      let labelKey = 'labelSold';
      if (variant.available) labelKey = preorder ? 'labelPreorder' : 'labelAdd';
      const label = this.addButton.dataset[labelKey];
      const labelEl = this.addButton.querySelector('span');
      if (label && labelEl) labelEl.textContent = label;
    }

    /* Low-stock lines (merchant feedback, 3 October 2026): from the count
       map when the variant has stock on hand, so the number is what's left
       after the cart's share; hidden once nothing is left or more is left
       than the threshold. */
    const lines = stock && left > 0 && this.preorder.lowStock ? this.preorder.lowStock[left] : null;
    if (this.lowStock) {
      if (lines) this.lowStock.textContent = lines[0];
      this.lowStock.hidden = !lines;
    }
    if (this.lowStockCta) {
      if (lines) this.lowStockCta.textContent = lines[1];
      this.lowStockCta.hidden = !lines;
    }

    const on = preorder || partial;
    const date = on ? this.shipByDate(Number(this.preorder.days) || 0) : null;
    if (this.preorderLine) {
      const template = partial ? this.preorder.partial : this.preorder && this.preorder.line;
      if (on && template) this.preorderLine.textContent = template.replace('[date]', this.formatDate(date, true));
      this.preorderLine.hidden = !on;
    }
    return preorder;
  }

  disconnectedCallback() {
    if (this.unsubscribeCart) this.unsubscribeCart();
  }

  /* Today in the studio's time zone plus the lead time, moved on to the
     Monday when it lands on a weekend, so the promise is always a day the
     studio can post. Calendar days, not business days: the lead time the
     merchant sets already carries the buffer. Built at noon UTC so no
     time-zone offset can tip it into the neighbouring day. The cart's
     assets/ship-by-gp.js repeats this rule: change one, change both. */
  shipByDate(days) {
    const today = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Australia/Sydney',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
    const [y, m, d] = today.split('-').map(Number);
    const date = new Date(Date.UTC(y, m - 1, d + days, 12));
    const weekday = date.getUTCDay();
    if (weekday === 6) date.setUTCDate(date.getUTCDate() + 2);
    if (weekday === 0) date.setUTCDate(date.getUTCDate() + 1);
    return date;
  }

  /* en-AU fixed rather than the page's lang ("en"), which formats US-style
     ("Tue, October 28"). */
  formatDate(date, withWeekday) {
    const options = { timeZone: 'UTC', day: 'numeric', month: 'long' };
    if (withWeekday) options.weekday = 'short';
    return new Intl.DateTimeFormat('en-AU', options).format(date).replace(',', '');
  }

  /* The form is in the DOM on every product page and hidden when the variant
     is available — a chip stays enabled whenever any available variant carries
     its value, so a sold-out combination is reachable with every chip
     selectable. Tags and note text come from the Liquid-built map, so the
     handle filter is never reimplemented here. */
  toggleBackInStock(variant) {
    if (!this.bis) return;
    this.bis.hidden = variant.available;
    if (variant.available) return;

    const tagInput = this.bis.querySelector('[data-bis-tags-input]');
    const note = this.bis.querySelector('[data-bis-note]');
    const tags = this.bisTags[variant.id];
    const noteText = this.bisTags[`${variant.id}-note`];
    if (tagInput && tags) tagInput.value = tags;
    if (note && noteText) note.textContent = noteText;
  }

  showMedia(mediaId) {
    const thumb = document.querySelector(`[data-gallery-thumb][data-media-id="${mediaId}"]`);
    const main = document.querySelector('[data-gallery-main]');
    if (!thumb || !main) return;

    const img = thumb.querySelector('img');
    const mainImg = main.querySelector('img');
    /* The full-size URL is emitted by Liquid (data-full-src), not derived by
       rewriting the thumbnail's own src with a regex — a pattern miss there
       would silently promote a 240px thumbnail to the hero image. Same
       Liquid-emits-it pattern already used for prices and back-in-stock tags. */
    if (mainImg && thumb.dataset.fullSrc) {
      mainImg.src = thumb.dataset.fullSrc;
      mainImg.srcset = '';
      mainImg.alt = (img && img.alt) || '';
    }
    main.dataset.mediaId = mediaId;

    document.querySelectorAll('[data-gallery-thumb]').forEach((b) => {
      const isCurrent = b === thumb;
      b.classList.toggle('is-current', isCurrent);
      b.setAttribute('aria-pressed', String(isCurrent));
    });
  }
}

if (!customElements.get('product-form-gp')) {
  customElements.define('product-form-gp', ProductFormGp);
}

/* Sticky add-to-cart bar (sections/main-product-gp.liquid). A remote control
   for the main form, never a second form: Add to cart clicks the main submit,
   so quantity, variant id, validation and Dawn's cart-drawer path in
   assets/product-form.js all run exactly as they would from the main button.

   - Visibility: shown once the main submit has scrolled up under the
     sticky header, hidden again as soon as it is back in view. A button still
     below the fold on first load does not count as "scrolled past". It also
     hides once the footer group reaches the viewport (merchant feedback
     round 8): the bar is ink, as is the footer, so at the foot of the page
     the two would merge into one band — and by then the shopper has left
     the product behind anyway.
   - Variant: follows the gp:variant-change event ProductFormGp dispatches,
     so price and availability are the same values the main column shows.
   - Loading: mirrors the main button's .loading class and aria-disabled,
     which product-form.js sets for the duration of the add.
   - Sold out: the button becomes the back-in-stock call to action and moves
     focus to that form's email field instead of submitting. */
class StickyAtcGp extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') || document;
    this.mainButton = this.section.querySelector('[data-add-button]');
    this.button = this.querySelector('[data-sticky-button]');
    /* No IntersectionObserver means no way to know when to show — stay
       hidden rather than covering the page permanently. */
    if (!this.mainButton || !this.button || !('IntersectionObserver' in window)) return;

    this.labelEl = this.button.querySelector('[data-sticky-label]');
    this.priceEl = this.querySelector('[data-sticky-price]');
    this.variantEl = this.querySelector('[data-sticky-variant]');
    this.variantWrap = this.querySelector('[data-sticky-variant-wrap]');
    this.variantPositions = (this.dataset.variantPositions || '')
      .split(',')
      .map(Number)
      .filter((position) => position > 0);
    this.spinner = this.button.querySelector('.loading__spinner');
    this.bis = this.section.querySelector('[data-bis]');
    this.available = this.dataset.available === 'true';
    this.preorder = this.dataset.preorder === 'true';
    this.visible = false;
    this.pastButton = false;
    this.footerInView = false;
    this.bisInView = false;

    this.onVariantChange = (e) => {
      const { variant, price, preorder } = e.detail || {};
      if (!variant) return;
      if (price && this.priceEl) this.priceEl.textContent = price;
      this.renderVariantLabel(variant);
      this.available = Boolean(variant.available);
      this.preorder = Boolean(preorder);
      this.render();
    };
    this.section.addEventListener('gp:variant-change', this.onVariantChange);

    this.onClick = () => {
      if (this.button.getAttribute('aria-disabled') === 'true') return;
      if (this.available) {
        this.addFromBar = true;
        this.mainButton.click();
      } else {
        this.goToBackInStock();
      }
    };
    this.button.addEventListener('click', this.onClick);

    /* An add started from the bar that fails: product-form.js reveals its
       .product-form__error-message-wrapper beside the main button, which is
       off screen by definition — so the shopper would see the spinner stop
       and nothing else. Bring the message into view. It publishes cartError
       BEFORE it un-hides the wrapper, so the scroll waits a frame. Only adds
       the bar started count; a failure from the main button is already in
       view. cartUpdate clears the flag on success. pubsub.js and
       constants.js are global and deferred ahead of this file. */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribeCartError = subscribe(PUB_SUB_EVENTS.cartError, () => {
        if (!this.addFromBar) return;
        this.addFromBar = false;
        requestAnimationFrame(() => this.revealError());
      });
      this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.addFromBar = false;
      });
    }

    /* Only the main button's attributes are watched, not its subtree —
       product-form.js toggles .loading and aria-disabled on the button
       itself. */
    this.loadingObserver = new MutationObserver(() => this.render());
    this.loadingObserver.observe(this.mainButton, {
      attributes: true,
      attributeFilter: ['class', 'aria-disabled', 'aria-haspopup'],
    });

    /* Merchant feedback round 9: the top of the observed area is pulled down
       by the sticky header's height, so a main button that has slid UNDER
       the header counts as gone — otherwise there was a header-height
       stretch where neither button was reachable. Measured once at connect;
       the header compacts a little on scroll, which only makes this trigger
       a few px early.

       Round 10 removed a second trigger (show once the next section enters
       view): it put two amber buttons on screen at once, against the brand's
       rule of one, and tied the timing to whichever section happens to come
       next. One rule at every width — the bar appears when the main button
       stops being reachable, and not before. */
    const header = document.querySelector('.section-header');
    const headerHeight = header ? Math.round(header.getBoundingClientRect().height) : 0;
    this.viewObserver = new IntersectionObserver(
      ([entry]) => {
        this.pastButton = !entry.isIntersecting && entry.boundingClientRect.top < headerHeight;
        this.updateVisibility();
      },
      { rootMargin: `-${headerHeight}px 0px 0px 0px` }
    );
    this.viewObserver.observe(this.mainButton);


    /* The first section of the footer group (the Acknowledgement strip
       today), so the bar is gone before any of the footer region shows.
       Falls back to Dawn's <footer> element. */
    const footer =
      document.querySelector('.shopify-section-group-footer-group') || document.querySelector('footer');
    if (footer) {
      this.footerObserver = new IntersectionObserver(([entry]) => {
        // In view OR already scrolled past (above the viewport): once the
        // shopper is in the footer the bar stays away, rather than coming
        // back when the footer group's first section scrolls off the top.
        this.footerInView = entry.isIntersecting || entry.boundingClientRect.top < 0;
        this.updateVisibility();
      });
      this.footerObserver.observe(footer);
    }

    /* While the back-in-stock form is itself on screen, the bar's "Notify
       me" would only point at it — so the bar stands down. Matters most when
       Past flavours links straight to #back-in-stock, which lands past the
       main button. A hidden form (available variant) never intersects. */
    if (this.bis) {
      this.bisObserver = new IntersectionObserver(([entry]) => {
        this.bisInView = entry.isIntersecting;
        this.updateVisibility();
      });
      this.bisObserver.observe(this.bis);
    }

    this.render();
  }

  disconnectedCallback() {
    /* The theme editor re-renders sections in place, so everything set up
       above has to come down cleanly. */
    if (this.section && this.onVariantChange) {
      this.section.removeEventListener('gp:variant-change', this.onVariantChange);
    }
    if (this.loadingObserver) this.loadingObserver.disconnect();
    if (this.viewObserver) this.viewObserver.disconnect();
    if (this.footerObserver) this.footerObserver.disconnect();
    if (this.bisObserver) this.bisObserver.disconnect();
    if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
    if (this.unsubscribeCartError) this.unsubscribeCartError();
    if (this.unsubscribeCartUpdate) this.unsubscribeCartUpdate();
  }

  revealError() {
    const wrapper = this.section.querySelector('.product-form__error-message-wrapper');
    if (!wrapper || wrapper.hidden) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    wrapper.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
  }

  /* The selected values of the options that actually vary (positions from
     data-variant-positions, set in Liquid), as plain words after the title —
     "Rainbow Sprinkle / Magnet" (the separator is the locale's
     sections.product.sticky_variant_separator, via data-variant-join). Read-only confirmation of what Add to cart
     will add, since the choices themselves are off screen by now. */
  renderVariantLabel(variant) {
    if (!this.variantEl || !Array.isArray(variant.options)) return;
    const words = this.variantPositions
      .map((position) => variant.options[position - 1])
      .filter(Boolean)
      .join(this.dataset.variantJoin || ' / ');
    this.variantEl.textContent = words;
    if (this.variantWrap) this.variantWrap.hidden = words === '';
  }

  updateVisibility() {
    this.setVisible(this.pastButton && !this.footerInView && !this.bisInView);
  }

  render() {
    const canNotify = Boolean(this.bis);
    const loading = this.available && this.mainButton.classList.contains('loading');
    const busy = this.available && this.mainButton.getAttribute('aria-disabled') === 'true';

    let labelKey = 'labelNotify';
    if (this.available) labelKey = this.preorder ? 'labelPreorder' : 'labelAdd';
    const label = this.dataset[labelKey];
    if (label && this.labelEl) this.labelEl.textContent = label;

    /* Sold out with no back-in-stock form to send people to: nothing useful
       to do, so the button is disabled like the main one. */
    this.button.disabled = !this.available && !canNotify;

    this.button.classList.toggle('loading', loading);
    if (this.spinner) this.spinner.classList.toggle('hidden', !loading);
    if (busy) {
      this.button.setAttribute('aria-disabled', 'true');
    } else {
      this.button.removeAttribute('aria-disabled');
    }

    /* product-form.js marks the main button as opening the cart drawer;
       the bar's button does the same thing when it adds, and nothing like it
       when it points at the back-in-stock form. */
    const popup = this.mainButton.getAttribute('aria-haspopup');
    if (this.available && popup) {
      this.button.setAttribute('aria-haspopup', popup);
    } else {
      this.button.removeAttribute('aria-haspopup');
    }
  }

  setVisible(visible) {
    if (visible === this.visible) return;
    this.visible = visible;
    this.classList.toggle('is-visible', visible);
    /* aria-hidden and inert together: out of the accessibility tree and out
       of the tab order while hidden. Setting inert also drops focus from
       anything inside, so a focused button never stays focused off-screen. */
    if (visible) {
      this.removeAttribute('aria-hidden');
    } else {
      this.setAttribute('aria-hidden', 'true');
    }
    this.toggleAttribute('inert', !visible);
  }

  goToBackInStock() {
    if (!this.bis || this.bis.hidden) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.bis.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    /* preventScroll: the scroll above already brings the form into view, and
       letting focus() scroll as well would cut the smooth scroll short. */
    const field = this.bis.querySelector('input[type="email"]');
    if (field) field.focus({ preventScroll: true });
  }
}

if (!customElements.get('sticky-atc-gp')) {
  customElements.define('sticky-atc-gp', StickyAtcGp);
}
